import { mdiContentCopy, mdiEmoticonSadOutline, mdiLinkVariant, mdiRestart, mdiShare } from "@mdi/js";
import Icon from "@mdi/react";
import {
  Avatar,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import AppBar from "components/AppBar";
import ContrastIcon from "components/ContrastIcon";
import PageLayout from "components/PageLayout";
import { getHost } from "utils/device";
import useShare from "hooks/useShare";
import useCopyToClipboard from "hooks/useCopyToClipboard";
import useNotifier from "hooks/useNotifier";
import { useEffect, useMemo, useState } from "react";
import GroupSlugsApi from "api/group-slugs";
import useBoundStore from "store";
import PageLoader from "components/PageLoader";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import ConfirmDialog from "components/ConfirmDialog";

interface GroupInviteLinkProps {
  onBack?: () => void;
}

function generateLink(slug: string | null) {
  return slug ? `${getHost()}/join-via-link#slug=${slug}` : null;
}

export default function GroupInviteLink(props: GroupInviteLinkProps) {
  const { onBack } = props;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [slug, setSlug] = useState<string | null>(null);
  const groupId = useBoundStore((state) => state.selected.groupId);
  const groups = useBoundStore((state) => state.appData.groups);
  const group = groups.find((g) => g.id === groupId);
  const { canCopy, copy } = useCopyToClipboard();
  const { canShare, share } = useShare();
  const { showNotification } = useNotifier();
  const groupInviteLinksApi = useMemo(() => new GroupSlugsApi(showNotification), [showNotification]);
  const inviteLink = generateLink(slug);

  useEffect(() => {
    (async () => {
      if (groupId) {
        const { slug: currentSlug } = await groupInviteLinksApi.getGroupSlug(groupId);

        if (!currentSlug) {
          const { slug: newSlug } = await groupInviteLinksApi.generateGroupSlug(groupId);
          setSlug(newSlug);
        } else {
          setSlug(currentSlug);
        }
      }
      setIsLoading(false);
    })();
  }, [groupId, groupInviteLinksApi]);

  function handleShare() {
    if (inviteLink) {
      share({
        title: "GroupUp - invite link",
        text: group?.name || undefined,
        url: inviteLink,
      });
    }
  }

  function handleCopy() {
    if (inviteLink) {
      copy(inviteLink);
      showNotification({ message: "Link copied", severity: "success" });
    }
  }

  async function handleReset() {
    if (groupId) {
      setIsGenerating(true);
      const { slug: newSlug, error } = await groupInviteLinksApi.generateGroupSlug(groupId);
      setSlug(newSlug);
      setIsGenerating(false);

      if (!error) {
        showNotification({
          message: "Link successfully generated.",
          severity: "success",
        });
      }
    }
    setConfirmDialogOpen(false);
  }

  function renderAppBar() {
    return <AppBar title='Invite via link' onBack={onBack} />;
  }

  if (isLoading) {
    return (
      <PageLayout appBar={renderAppBar()}>
        <PageLoader />
      </PageLayout>
    );
  }

  if (!inviteLink) {
    return (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder icon={mdiEmoticonSadOutline} text='Group invite link is unavailable' />
      </PageLayout>
    );
  }

  return (
    <PageLayout appBar={renderAppBar()}>
      <List>
        <ListItem>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: "primary.main" }}>
              {isGenerating ? (
                <CircularProgress sx={{ color: "text.primary" }} size={24} />
              ) : (
                <ContrastIcon path={mdiLinkVariant} title='Add' size={1} />
              )}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Typography color='text.secondary' variant='caption'>
                {inviteLink}
              </Typography>
            }
          />
        </ListItem>

        {canCopy && (
          <ListItemButton onClick={handleCopy}>
            <ListItemIcon>
              <Icon path={mdiContentCopy} size={1} />
            </ListItemIcon>
            <ListItemText primary='Copy link' />
          </ListItemButton>
        )}

        {canShare && (
          <ListItemButton onClick={handleShare}>
            <ListItemIcon>
              <Icon path={mdiShare} size={1} />
            </ListItemIcon>
            <ListItemText primary='Share link' />
          </ListItemButton>
        )}

        <ListItemButton onClick={() => setConfirmDialogOpen(true)} disabled={isGenerating}>
          <ListItemIcon>
            <Icon path={mdiRestart} size={1} />
          </ListItemIcon>
          <ListItemText primary='Reset link' />
        </ListItemButton>
      </List>

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        title='Reset link'
        body={
          <Typography>
            Are you sure you want to reset the invite link for{" "}
            <Typography component='span' color='primary.main'>
              {group?.name}
            </Typography>{" "}
            group? If you reset the link, no one will be able to use the old link to join this group.
          </Typography>
        }
        closeLabel='Cancel'
        confirmLabel='Reset'
        onConfirm={handleReset}
        shouldSetLoading={false}
      />
    </PageLayout>
  );
}
